import apiClient from './client';
import type { ModuleFamily, QuestionFormat } from '../config/moduleCatalog';

/** One revealed call in the auction. `alert` threads the Bid Alert standard through. */
export interface ModuleAuctionBid {
  seat: string;
  call: string;
  alert: string | null;
}

export interface ModuleHand {
  spades: string;
  hearts: string;
  diamonds: string;
  clubs: string;
}

export interface ModuleQuestion {
  partnershipHandId: number;
  /** cardsgamehands `tableresults.id`. Served to everyone; only Admin/Editor sessions show it. */
  sourceTableResultId: number;
  questionFormat: QuestionFormat;
  prompt: string;
  hand: ModuleHand;
  auction: ModuleAuctionBid[];
  vulnerability: string;
  dealer: string;
  decisionSeat: string;
  position: number;
  poolSize: number;
  stepIndex: number | null;
  stepCount: number | null;
  difficulty: number | null;
  /** Canonical scoring label — "MPs", "IMPs", "BAM" (feature 024's vocabulary, resolved server-side). */
  scoringType: string;
  /** The tournament the hand was played in; null when the board has none. */
  tournamentTitle: string | null;
  /** True when the pool holds nothing unseen — the UI shows the explicit exhausted state. */
  exhausted: boolean;
}

export interface ModuleAnswerResult {
  isCorrect: boolean;
  correctAnswer: string;
  expertComment: string | null;
  /**
   * Feature 027 — the AI engine's reasoning for the call just answered, but ONLY where it
   * predicted the call Jeff actually made. Null when it predicted otherwise, when the hand was
   * never analysed, or when `expertComment` is present (Jeff's own words win). Raw reasoning:
   * the page adds the "AI predicted as Jeff because" prefix, and shows "Information not
   * available" in its place when this is null.
   */
  aiComment: string | null;
  /** Multi-step only: the next step of the same hand, or null when the hand is finished. */
  nextStep: ModuleQuestion | null;
  exhausted: boolean;
  /**
   * Yes/No modules only: the call Jeff actually made — his opening bid, or "Pass" when he
   * declined to open. Null for call modules, where `correctAnswer` already is that call.
   */
  openingBid: string | null;
}

function path(family: ModuleFamily, topicKey: string, moduleSlug: string): string {
  return `/module-practice/${family}/${topicKey}/${moduleSlug}`;
}

export const modulePracticeApi = {
  getNext: async (
    family: ModuleFamily,
    topicKey: string,
    moduleSlug: string
  ): Promise<ModuleQuestion> => {
    const response = await apiClient.get<ModuleQuestion>(
      `${path(family, topicKey, moduleSlug)}/next`
    );
    return response.data;
  },

  /**
   * One specific tournament hand by its `tableresults.id` — the editor/admin URL locator
   * (`?id=`). Served regardless of answer history. 403 for non-editors, 404 when the id is not
   * part of this module's pool.
   */
  getById: async (
    family: ModuleFamily,
    topicKey: string,
    moduleSlug: string,
    id: number
  ): Promise<ModuleQuestion> => {
    const response = await apiClient.get<ModuleQuestion>(
      `${path(family, topicKey, moduleSlug)}/by-id/${id}`
    );
    return response.data;
  },

  submitAnswer: async (
    family: ModuleFamily,
    topicKey: string,
    moduleSlug: string,
    body: { partnershipHandId: number; answer: string; stepIndex?: number | null }
  ): Promise<ModuleAnswerResult> => {
    const response = await apiClient.post<ModuleAnswerResult>(
      `${path(family, topicKey, moduleSlug)}/answer`,
      body
    );
    return response.data;
  },

  /**
   * Moves past a hand without answering it. The skip is recorded, so the hand is not served
   * again — the pool advances exactly as it would after an answer, only without a verdict.
   */
  skip: async (
    family: ModuleFamily,
    topicKey: string,
    moduleSlug: string,
    partnershipHandId: number
  ): Promise<void> => {
    await apiClient.post(`${path(family, topicKey, moduleSlug)}/skip`, { partnershipHandId });
  },
};
